import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { IoMdClose } from "react-icons/io";
import { FaArrowDown, FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { ImSpinner2 } from "react-icons/im";
import { useUserContext } from "../../context/UserContext";
import { CSVLink } from "react-csv";
import CommentCell from "./CommentCell.jsx";

/** One row per check (latest status), with wall-clock duration for that check. */
function summarizeHistoryRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const groups = new Map();
  for (const row of rows) {
    const key = row.check || `__row_${row.id ?? groups.size}`;
    if (!groups.has(key)) {
      groups.set(key, { first: row, last: row, count: 1 });
    } else {
      const g = groups.get(key);
      g.last = row;
      g.count += 1;
    }
  }

  return [...groups.values()].map(({ first, last, count }) => {
    const firstMs = new Date(first.last_run).getTime();
    const lastMs = new Date(last.last_run).getTime();
    const fromTimestamps =
      Number.isFinite(firstMs) && Number.isFinite(lastMs)
        ? Math.max(0, Math.floor((lastMs - firstMs) / 1000))
        : null;
    const stored = Math.max(0, Math.floor(Number(last.duration) || 0));
    // Prefer wall-clock span when the check was observed more than once
    // (running → passed/failed). That matches the live phase timer.
    const duration = count > 1 && fromTimestamps != null ? fromTimestamps : stored;
    return { ...last, duration };
  });
}

function formatDurationSec(sec) {
  if (sec == null || Number.isNaN(Number(sec))) return "—";
  return `${Math.max(0, Math.floor(Number(sec)))}s`;
}

const STATUS_BADGES = {
  passed: "bg-green-100 text-green-800 border-green-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  running: "bg-sky-100 text-sky-800 border-sky-200",
  waiting: "bg-gray-100 text-gray-500 border-gray-200",
  skipped: "bg-gray-50 text-gray-400 border-gray-100",
  aborted: "bg-orange-100 text-orange-800 border-orange-200",
};

function StatusBadge({ status }) {
  if (!status) return <span className="text-gray-400">—</span>;
  const style =
    STATUS_BADGES[status] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded border text-sm font-semibold capitalize ${style}`}
    >
      {status}
    </span>
  );
}

function ResultBadge({ result }) {
  if (!result) return <span className="text-gray-400">—</span>;
  const tone =
    result === "success"
      ? "bg-green-500"
      : result === "failed"
        ? "bg-red-500"
        : "bg-orange-500";
  return (
    <span
      className={`inline-block min-w-[5rem] px-2.5 py-1 rounded text-sm font-semibold uppercase text-white ${tone}`}
    >
      {result}
    </span>
  );
}

export default function TestHistoryModal({ visible, onClose }) {
  const { deviceId, scenario, subscenario } = useUserContext();
  const [testList, setTestList] = useState([]);
  const [testId, setTestId] = useState();
  const [testDetails, setTestDetails] = useState();
  const [loadingTests, setLoadingTests] = useState(false);
  const [filterDate, setFilterDate] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [noItems, setNoItems] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    if (!visible || !deviceId) return setFilterDate("");
    let API = new URL(
      `${process.env.REACT_APP_SCRIPT_API_URL}/testing/get-test-list`,
    );
    API.searchParams.append("pageNumber", pageNumber);
    API.searchParams.append("pageSize", pageSize);
    setLoadingTests(true);
    (async () => {
      let body = {
        deviceId,
        scenario,
        subScenario: subscenario,
      };
      if (filterDate && filterDate !== "") body.date = filterDate;
      try {
        let res = await axios.post(API.toString(), body);
        if (res.status !== 200)
          throw new Error(res.data?.data?.message ?? res.statusText);
        setTestList(res.data);
        setNoItems(res.data.length === 0);
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message ?? e.message);
      } finally {
        setLoadingTests(false);
      }
    })();
  }, [visible, deviceId, filterDate, pageNumber, scenario, subscenario]);

  useEffect(() => {
    setPageNumber(1);
    setNoItems(false);
  }, [filterDate, deviceId]);

  useEffect(() => {
    setTestList([]);
    setTestId();
    setTestDetails();
    setNoItems(false);
    setFilterDate("");
  }, [deviceId]);

  useEffect(() => {
    if (!testId || !visible) return;
    setLoadingTests(true);
    (async () => {
      try {
        let res = await axios.get(
          `${process.env.REACT_APP_SCRIPT_API_URL}/testing/test-details/${testId}`,
        );
        if (res.status !== 200)
          throw new Error(res.data?.data?.message ?? res.statusText);
        setTestDetails(res.data);
        if (res.data.length === 0) setNoItems(true);
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.message ?? e.message);
      } finally {
        setLoadingTests(false);
      }
    })();
  }, [visible, testId]);

  if (!visible) return null;

  const historyRows = testDetails ? summarizeHistoryRows(testDetails) : [];

  return (
    <div className="overflow-hidden flex flex-col mt-6 w-full max-h-[calc(100vh-13rem)] bg-white rounded border border-gray-200 shadow-sm">
      <div className="flex flex-wrap gap-3 items-center py-3 px-4 md:px-5 text-base bg-gray-50 border-b border-gray-200 shrink-0">
        <span className="text-gray-700">Testing History for DeviceId:</span>
        <button
          type="button"
          onClick={onClose}
          className="font-semibold text-indigo-700 break-all hover:underline"
        >
          {deviceId}
        </button>
        <div className="hidden sm:block w-px h-5 bg-gray-300" />
        <span className="capitalize text-gray-800">{scenario}</span>
        {subscenario && subscenario !== "" && (
          <>
            <div className="hidden sm:block w-px h-5 bg-gray-300" />
            <span className="capitalize text-gray-800">{subscenario}</span>
          </>
        )}
        <div className="hidden md:block w-px h-5 bg-gray-300" />
        <div className="flex items-center gap-2">
          <span className="text-gray-600">Filter:</span>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="p-1.5 text-sm rounded border border-gray-300 bg-white"
          />
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-2xl rounded hover:bg-gray-200"
          aria-label="Close"
        >
          <IoMdClose />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-5 pb-8" id="container">
        {testDetails ? (
          <div className="py-4 w-full min-w-0">
            <div className="flex flex-wrap gap-3 items-center mb-4">
              <button
                type="button"
                onClick={() => {
                  setTestDetails(null);
                  setTestId(null);
                }}
                className="flex gap-1 items-center text-indigo-700 hover:underline"
              >
                <FaChevronLeft />
                <span>View List</span>
              </button>
              <div className="flex-1" />
              <CSVLink
                data={historyRows}
                filename={`${testId}_${new Date().toISOString()}.csv`}
                target="_blank"
                className="flex gap-1 items-center py-1.5 px-3 text-sm font-medium bg-indigo-100 text-indigo-800 rounded hover:bg-indigo-200"
              >
                Download CSV
                <FaArrowDown />
              </CSVLink>
            </div>

            <div className="overflow-hidden rounded border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] border-collapse text-base">
                  <thead className="bg-gray-50 sticky top-0 z-[1]">
                    <tr className="text-left text-sm font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
                      <th className="px-4 py-3 w-[16%]">Checks</th>
                      <th className="px-4 py-3 w-[10%]">Status</th>
                      <th className="px-4 py-3 w-[16%]">Last Run</th>
                      <th className="px-4 py-3 w-[10%]">Duration</th>
                      <th className="px-4 py-3 w-[32%]">Comments</th>
                      <th className="px-4 py-3 w-[10%] text-center">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((log, i) => (
                      <tr
                        key={`${log.check}-${i}`}
                        className="align-top border-b border-gray-100 last:border-0 odd:bg-white even:bg-gray-50/70"
                      >
                        <td className="px-4 py-3 text-lg font-medium capitalize text-sky-700 break-words">
                          {log.check}
                        </td>
                        <td className="px-4 py-3 align-middle whitespace-nowrap">
                          <StatusBadge status={log.status} />
                        </td>
                        <td className="px-4 py-3 break-words text-gray-600 whitespace-nowrap">
                          {log.last_run
                            ? new Date(log.last_run).toLocaleString()
                            : "N/A"}
                        </td>
                        <td className="px-4 py-3 font-mono tabular-nums text-gray-700 whitespace-nowrap">
                          {formatDurationSec(log.duration)}
                        </td>
                        <td className="px-4 py-3 align-top text-gray-700">
                          <CommentCell text={log.comments} />
                        </td>
                        <td className="px-4 py-3 align-middle text-center">
                          <ResultBadge result={log.result} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : loadingTests ? (
          <div className="flex justify-center items-center py-24 w-full">
            <ImSpinner2 className="text-5xl animate-spin text-indigo-500" />
          </div>
        ) : noItems ? (
          <div className="w-full text-xl">
            <p className="my-20 text-center text-gray-500">No Logs Found</p>
            {pageNumber > 1 && (
              <div className="flex gap-5 justify-center items-center w-full">
                <button
                  type="button"
                  className="flex gap-1 items-center py-1.5 px-3 bg-indigo-100 text-indigo-800 rounded"
                  onClick={() => setPageNumber(pageNumber - 1)}
                >
                  <FaChevronLeft />
                  <span>Prev</span>
                  <span className="px-3 bg-indigo-50">{pageNumber}</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-hidden mt-4 rounded border border-gray-200">
              <table className="w-full min-w-[720px] border-collapse text-base">
                <thead className="bg-gray-50">
                  <tr className="text-left text-sm font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
                    <th className="px-4 py-3">Start</th>
                    <th className="px-4 py-3">End</th>
                    <th className="px-4 py-3">Duration</th>
                    <th className="px-4 py-3">Test Id</th>
                  </tr>
                </thead>
                <tbody>
                  {testList.map((log, i) => {
                    const startMs = new Date(log.start_timestamp).getTime();
                    const endMs = new Date(log.end_timestamp).getTime();
                    const durationSec =
                      Number.isFinite(startMs) && Number.isFinite(endMs)
                        ? Math.max(0, Math.floor((endMs - startMs) / 1000))
                        : null;

                    return (
                      <tr
                        key={log.test_id || i}
                        onClick={() => setTestId(log.test_id)}
                        className="border-b border-gray-100 last:border-0 odd:bg-white even:bg-gray-50/70 cursor-pointer hover:bg-sky-50"
                      >
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {new Date(log.start_timestamp).toLocaleString("en-GB")}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {new Date(log.end_timestamp).toLocaleString("en-GB")}
                        </td>
                        <td className="px-4 py-3 font-mono tabular-nums text-gray-700 whitespace-nowrap">
                          {formatDurationSec(durationSec)}
                        </td>
                        <td className="px-4 py-3 font-medium text-indigo-700 break-all">
                          {log.test_id}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex gap-5 justify-center items-center py-6 w-full">
              {pageNumber > 1 && (
                <button
                  type="button"
                  className="flex gap-1 items-center py-1.5 px-3 bg-indigo-100 text-indigo-800 rounded"
                  onClick={() => setPageNumber(pageNumber - 1)}
                >
                  <FaChevronLeft />
                  <span>Prev</span>
                </button>
              )}
              <div className="px-3 py-1 bg-indigo-50 text-indigo-800 rounded">
                {pageNumber}
              </div>
              {testList?.length === pageSize && (
                <button
                  type="button"
                  className="flex gap-1 items-center py-1.5 px-3 bg-indigo-100 text-indigo-800 rounded"
                  onClick={() => setPageNumber(pageNumber + 1)}
                >
                  <span>Next</span>
                  <FaChevronRight />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
